import { useMemo, useState, useCallback } from "react";
import { CaretUp, CaretDown, Funnel, FunnelSimple, X } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";

/**
 * Hook que administra ordenamiento y filtros por columna estilo Excel.
 *
 * @param {Array} rows        Filas originales sin filtrar
 * @param {Object} accessors  Objeto { colKey: (row) => valueForColumn }
 * @returns {
 *   sortedRows,   // filas ya filtradas y ordenadas
 *   sort,         // { col, dir: "asc"|"desc"|null }
 *   toggleSort,   // (col) => void  · cicla asc → desc → none
 *   filters,      // { col: Set(values) }  · vacío = mostrar todo
 *   setFilter,    // (col, Set)  · reemplaza el filtro
 *   clearFilter,  // (col) => void
 *   clearAll,     // () => void
 *   uniqueValues, // (col) => [{ raw, label }] únicos ordenados
 * }
 */
export function useExcelTable(rows, accessors) {
  const [sort, setSort] = useState({ col: null, dir: null });
  const [filters, setFilters] = useState({}); // { colKey: Set<string> }

  const toggleSort = useCallback((col) => {
    setSort((s) => {
      if (s.col !== col) return { col, dir: "asc" };
      if (s.dir === "asc") return { col, dir: "desc" };
      return { col: null, dir: null };
    });
  }, []);

  const setFilter = useCallback((col, set) => {
    setFilters((f) => {
      const next = { ...f };
      if (!set || set.size === 0) delete next[col];
      else next[col] = set;
      return next;
    });
  }, []);

  const clearFilter = useCallback((col) => {
    setFilters((f) => {
      if (!(col in f)) return f;
      const next = { ...f };
      delete next[col];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setFilters({});
    setSort({ col: null, dir: null });
  }, []);

  const uniqueValues = useCallback(
    (col) => {
      const acc = accessors?.[col];
      if (!acc) return [];
      const seen = new Map();
      for (const r of rows) {
        const raw = acc(r);
        const key = raw === null || raw === undefined || raw === "" ? "(vacío)" : String(raw);
        if (!seen.has(key)) seen.set(key, { raw: key, label: key });
      }
      return Array.from(seen.values()).sort((a, b) =>
        a.label.localeCompare(b.label, "es", { numeric: true, sensitivity: "base" }),
      );
    },
    [rows, accessors],
  );

  const sortedRows = useMemo(() => {
    // 1) Filtrar
    let list = rows;
    const activeFilters = Object.entries(filters);
    if (activeFilters.length > 0) {
      list = list.filter((r) =>
        activeFilters.every(([col, set]) => {
          const acc = accessors?.[col];
          if (!acc) return true;
          const raw = acc(r);
          const key = raw === null || raw === undefined || raw === "" ? "(vacío)" : String(raw);
          return set.has(key);
        }),
      );
    }
    // 2) Ordenar
    if (sort.col && sort.dir) {
      const acc = accessors?.[sort.col];
      if (acc) {
        list = [...list].sort((a, b) => {
          const va = acc(a);
          const vb = acc(b);
          // Números primero
          const na = typeof va === "number" ? va : parseFloat(va);
          const nb = typeof vb === "number" ? vb : parseFloat(vb);
          if (!isNaN(na) && !isNaN(nb) && String(va).trim() !== "" && String(vb).trim() !== "") {
            return sort.dir === "asc" ? na - nb : nb - na;
          }
          const sa = (va || "").toString().toLowerCase();
          const sb = (vb || "").toString().toLowerCase();
          return sort.dir === "asc" ? sa.localeCompare(sb, "es") : sb.localeCompare(sa, "es");
        });
      }
    }
    return list;
  }, [rows, filters, sort, accessors]);

  return { sortedRows, sort, toggleSort, filters, setFilter, clearFilter, clearAll, uniqueValues };
}

/**
 * Header de columna con sort + filtro tipo Excel.
 *
 * @param {Object} props
 * @param {string} props.col       Clave interna
 * @param {string} props.label     Etiqueta visible
 * @param {"left"|"right"|"center"} [props.align="left"]
 * @param {"asc"|"desc"|null} props.sortDir  Dirección actual (o null si no está ordenado por esta col)
 * @param {Function} props.onSort  Callback para togglear sort
 * @param {Array<{raw,label}>} props.options  Opciones únicas para el filtro
 * @param {Set<string>} props.selected  Valores actualmente permitidos (vacío = todo)
 * @param {Function} props.onFilterChange  (Set) => void
 * @param {Function} props.onClearFilter  () => void
 * @param {string} [props.className]
 * @param {string} [props.testId]
 */
export function ExcelTh({
  col, label, align = "left",
  sortDir, onSort,
  options = [], selected, onFilterChange, onClearFilter,
  className = "", testId,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const hasFilter = selected && selected.size > 0;

  // Estado local del popover: al abrir, si no había filtro, arrancamos con TODOS seleccionados.
  // Al aplicar, si están todos → limpiamos el filtro (equivalente a "todo").
  const [draft, setDraft] = useState(new Set());
  const openPopover = (v) => {
    setOpen(v);
    if (v) {
      setDraft(hasFilter ? new Set(selected) : new Set(options.map((o) => o.raw)));
      setQ("");
    }
  };

  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;

  const allSelected = draft.size === options.length;
  const toggleAll = () => {
    if (allSelected) setDraft(new Set());
    else setDraft(new Set(options.map((o) => o.raw)));
  };
  const toggleOne = (raw) => {
    const next = new Set(draft);
    if (next.has(raw)) next.delete(raw); else next.add(raw);
    setDraft(next);
  };
  const applyFilter = () => {
    if (draft.size === 0 || draft.size === options.length) {
      onClearFilter?.();
    } else {
      onFilterChange?.(draft);
    }
    setOpen(false);
  };

  const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const flexAlign = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";

  return (
    <th className={`${alignClass} px-3 py-2 ${className}`} data-testid={testId}>
      <div className={`inline-flex items-center gap-1 ${flexAlign} group`}>
        <button
          type="button"
          onClick={() => onSort?.(col)}
          className="inline-flex items-center gap-0.5 hover:text-neutral-900 transition-colors"
          data-testid={testId ? `${testId}-sort` : undefined}
        >
          <span>{label}</span>
          {sortDir === "asc" && <CaretUp size={11} weight="bold" className="text-emerald-700" />}
          {sortDir === "desc" && <CaretDown size={11} weight="bold" className="text-emerald-700" />}
          {!sortDir && <CaretUp size={11} weight="light" className="text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity" />}
        </button>
        <Popover open={open} onOpenChange={openPopover}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`p-0.5 rounded-sm hover:bg-neutral-200 transition-colors ${hasFilter ? "text-emerald-700" : "text-neutral-400 opacity-0 group-hover:opacity-100"}`}
              data-testid={testId ? `${testId}-filter` : undefined}
              aria-label={`Filtrar ${label}`}
            >
              {hasFilter ? <Funnel size={11} weight="fill" /> : <FunnelSimple size={11} weight="regular" />}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest font-mono text-neutral-500">Filtrar</div>
              {hasFilter && (
                <button
                  type="button"
                  onClick={() => { onClearFilter?.(); setOpen(false); }}
                  className="text-[10px] text-red-600 hover:underline flex items-center gap-1"
                  data-testid={testId ? `${testId}-filter-clear` : undefined}
                >
                  <X size={10} /> Limpiar
                </button>
              )}
            </div>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar…"
              className="h-7 text-xs mb-2"
              autoFocus
            />
            <label className="flex items-center gap-2 px-1 py-1 text-xs font-medium border-b border-neutral-200 mb-1">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
              />
              <span>(Todos)</span>
            </label>
            <ScrollArea className="h-48">
              <div className="space-y-0.5 pr-2">
                {filtered.map((o) => (
                  <label key={o.raw} className="flex items-center gap-2 px-1 py-0.5 text-xs hover:bg-neutral-50 rounded-sm cursor-pointer">
                    <Checkbox
                      checked={draft.has(o.raw)}
                      onCheckedChange={() => toggleOne(o.raw)}
                    />
                    <span className="truncate">{o.label}</span>
                  </label>
                ))}
                {filtered.length === 0 && (
                  <div className="text-[11px] text-neutral-500 italic px-1 py-2">Sin coincidencias</div>
                )}
              </div>
            </ScrollArea>
            <div className="flex justify-end gap-1 mt-2 pt-2 border-t border-neutral-200">
              <Button
                type="button" variant="ghost" size="sm" className="h-7 text-xs"
                onClick={() => setOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button" size="sm" className="h-7 text-xs"
                onClick={applyFilter}
                data-testid={testId ? `${testId}-filter-apply` : undefined}
              >
                Aplicar
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </th>
  );
}
